import { LinkOutlined } from "@ant-design/icons"
import { type ItemType, type MenuItemType } from "antd/es/menu/interface"
import { type ColumnType } from "antd/es/table/interface"
import React from "react"

import Icon from "../../../../Icon"
import { RowActionsMenu } from "../../../../components/EntityTable/RowActionsMenu.tsx"
import { type ProjectListItem } from "../../types/types.ts"

interface GetProjectsColumnActionsProps {
  t: (key: string) => string
  editAllowed?: boolean
  archiveAllowed?: boolean
  restoreAllowed?: boolean
  deleteAllowed?: boolean
  onSelectRow: (record: ProjectListItem, action: string) => Promise<void> | void
}

export const getProjectsColumnActions = ({
  t,
  editAllowed,
  archiveAllowed,
  restoreAllowed,
  deleteAllowed,
  onSelectRow
}: GetProjectsColumnActionsProps): ColumnType<ProjectListItem> => {
  const getItems = (record: ProjectListItem) => {
    const menuItems: ItemType<MenuItemType>[] = []

    // View and Manage open the same page, so View is offered only where
    // Manage is not: to a caller without edit rights, and on the Archive. It
    // mirrors the ungated name-click Link, so it carries no permission itself.
    if (!editAllowed) {
      menuItems.push({
        key: "view",
        label: t("projectActions.view"),
        icon: <LinkOutlined />,
        onClick: () => onSelectRow(record, "view")
      })
    }

    // Delete is offered on the Archive only — see `columns.tsx`, which passes
    // `deleteAllowed` just for that view.
    if (editAllowed) {
      // Manage routes to the workspace index, not a named tab: there is no
      // separate "manage" right, and every tab adds a view right of its own.
      menuItems.push({
        key: "edit",
        label: t("projectActions.editDetails"),
        icon: <Icon type={"Edit"} />,
        onClick: () => onSelectRow(record, "edit")
      })
      menuItems.push({
        key: "manage",
        label: t("projectActions.manage"),
        icon: <Icon type={"Settings"} />,
        onClick: () => onSelectRow(record, "manage")
      })
    }

    // Keyed on the flag, not the status: EAD-642 retired status 7, so an
    // archived project still reads Completed or Cancelled.
    if (archiveAllowed && !record.is_archived) {
      // Gated on editAllowed, not menuItems.length: a lone View item above
      // Archive would otherwise get a divider directly under it.
      if (editAllowed)
        menuItems.push({ type: "divider", key: "divider-archive" })
      menuItems.push({
        key: "archive",
        label: t("projectArchive.action"),
        danger: true,
        icon: <Icon type={"Archive"} />,
        onClick: () => onSelectRow(record, "archive")
      })
    }

    if (restoreAllowed) {
      menuItems.push({
        key: "restore",
        label: t("projectArchive.restore"),
        icon: <Icon type={"Restore"} />,
        onClick: () => onSelectRow(record, "restore")
      })
    }

    if (deleteAllowed) {
      const blockedReason = record.delete_blocked_reason
      // `can_delete` and the reason are two independent serializer fields, so
      // neither implies the other: a row blocked without a reason string would
      // otherwise render an enabled Delete that 400s on confirm.
      const blocked = record.can_delete === false || !!blockedReason
      menuItems.push({
        key: "delete",
        label: t("projectDelete.delete"),
        danger: true,
        icon: <Icon type={"Delete"} />,
        disabled: blocked,
        // A plain `title` attribute rather than antd's Tooltip: a disabled
        // menu item still renders it on hover, no pointer-events wrapper needed.
        title: blocked
          ? blockedReason || t("projectDelete.blocked")
          : undefined,
        onClick: () => onSelectRow(record, "delete")
      })
    }

    return menuItems
  }

  return {
    title: "Actions",
    // View has no permission gate, so unlike before, this column is never
    // hidden outright.
    fixed: "right",
    width: 60,
    align: "right",
    render: (_, record) => {
      const items = getItems(record)

      if (!items.length) return null

      return (
        <RowActionsMenu
          items={items}
          buttonProps={{ "aria-label": t("tableActions.rowMenu") }}
        />
      )
    }
  }
}
